import React from 'react';
import { motion } from 'framer-motion';
import { Timer, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const fade = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.25 },
};

export const ServiceStep = ({
  services,
  selectedService,
  setSelectedService,
  isExistingClient,
  showRecurringWarning,
}) => (
  <motion.div
    key="s1"
    {...fade}
    className="bg-white rounded-2xl p-6 shadow-sm border border-zinc-100"
    data-testid="step-service"
  >
    <h2 className="text-lg sm:text-xl font-bold text-zinc-900 font-heading mb-1">
      Select Your Service
    </h2>
    <p className="text-sm text-zinc-500 mb-5">Choose the cleaning that fits your needs</p>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {services.map((svc) => {
        const isActive = selectedService?.id === svc.id;
        const isDisabledRecurring =
          (svc.type === 'weekly' || svc.type === 'biweekly') && !isExistingClient;
        return (
          <button
            key={svc.id}
            onClick={() => !isDisabledRecurring && setSelectedService(svc)}
            disabled={isDisabledRecurring}
            className={`relative p-4 rounded-xl border-2 text-left transition-all group ${
              isActive
                ? 'border-[#F56F05] bg-[#FFF7ED] shadow-md shadow-orange-100'
                : isDisabledRecurring
                  ? 'border-zinc-100 bg-zinc-50 opacity-60 cursor-not-allowed'
                  : 'border-zinc-100 hover:border-[#F56F05]/40 hover:shadow-sm'
            }`}
            data-testid={`service-card-${svc.id}`}
          >
            {svc.badge && (
              <Badge className="absolute -top-2.5 right-2 bg-[#F56F05] text-white text-[9px] px-2">
                {svc.badge}
              </Badge>
            )}
            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${
                isActive ? 'bg-[#F56F05] text-white' : 'bg-zinc-100 text-zinc-400 group-hover:bg-zinc-200'
              }`}
            >
              <svc.icon className="h-4 w-4" />
            </div>
            <h3 className="font-bold text-zinc-900 text-sm mb-0.5">{svc.name}</h3>
            <p className="text-[11px] text-zinc-500 leading-snug mb-2">{svc.desc}</p>
            <span className="text-[10px] text-zinc-400 flex items-center gap-1">
              <Timer className="h-3 w-3" />
              {svc.time}
            </span>
            {isDisabledRecurring && (
              <span className="inline-block mt-1 text-[10px] bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-full">
                Existing clients only
              </span>
            )}
          </button>
        );
      })}
    </div>

    {showRecurringWarning && (
      <div
        className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3"
        data-testid="recurring-warning"
      >
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-amber-800 text-sm">
            Recurring maintenance is available after your first Deep Reset.
          </p>
          <Button
            onClick={() => setSelectedService(services.find((s) => s.id === 'deep_reset'))}
            className="mt-2 bg-[#F56F05] hover:bg-[#C2410C] text-white text-xs h-8"
            data-testid="book-deep-reset-instead"
          >
            Book Deep Reset Instead
          </Button>
        </div>
      </div>
    )}
  </motion.div>
);
